import { useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, ShieldAlert, Loader2, Send, Bell } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMPEL_STYLES = {
  gruen: {
    label: "GRÜN",
    hint: "Stabil — Routine fortführen",
    Icon: CheckCircle2,
    bar: "bg-emerald-500",
    box: "border-emerald-400/40 bg-emerald-50",
    accent: "text-emerald-700",
  },
  gelb: {
    label: "GELB",
    hint: "Auffällig — engmaschig beobachten",
    Icon: AlertTriangle,
    bar: "bg-amber-500",
    box: "border-amber-400/40 bg-amber-50",
    accent: "text-amber-700",
  },
  rot: {
    label: "ROT",
    hint: "Kritisch — sofort handeln & melden",
    Icon: ShieldAlert,
    bar: "bg-brand-darkred",
    box: "border-brand-darkred/50 bg-peach-50",
    accent: "text-brand-darkred",
  },
};

export default function PkiAmpel() {
  const [beobachtung, setBeobachtung] = useState("");
  const [kunde, setKunde] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (beobachtung.trim().length < 8) {
      setError("Bitte einen kurzen Satz zur Beobachtung eintragen (mindestens 8 Zeichen).");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/ampel`, {
        beobachtung: beobachtung.trim(),
        kunde: kunde.trim() || null,
      }, { withCredentials: true });
      setResult(r.data);
      // → 360°-Klientenakte (localStorage)
      if (kunde.trim()) {
        try {
          const store = JSON.parse(localStorage.getItem("dh-klienten-akte") || "{}");
          const arr = store[kunde.trim()] || [];
          arr.unshift({ typ: "ampel", ts: new Date().toISOString(), ...r.data });
          store[kunde.trim()] = arr.slice(0, 20);
          localStorage.setItem("dh-klienten-akte", JSON.stringify(store));
        } catch { /* noop */ }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Konnte die Ampel nicht bewerten. Bitte gleich noch mal versuchen.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setBeobachtung("");
    setError(null);
  };

  return (
    <div data-testid="pki-ampel" className="space-y-4">
      <div>
        <h2 className="font-display text-2xl text-sand-800 leading-tight">Pflegeampel</h2>
        <p className="text-[13.5px] text-sand-600 mt-1 leading-relaxed">
          In einem Satz die Beobachtung eintragen — die KI bewertet sofort mit Grün / Gelb / Rot,
          nennt konkrete Sofortmaßnahmen und wer zu informieren ist.
        </p>
      </div>

      {!result && (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label htmlFor="pki-ampel-kunde" className="text-[12px] uppercase tracking-[0.16em] font-semibold text-sand-700">
              Bewohner / Kunde (optional)
            </label>
            <input
              id="pki-ampel-kunde"
              type="text"
              value={kunde}
              onChange={(e) => setKunde(e.target.value)}
              data-testid="pki-ampel-kunde"
              placeholder="z. B. Herr Müller"
              className="mt-1 w-full h-11 px-3 rounded-xl bg-white border border-cream-300 focus:border-brand-darkred focus:outline-none text-[15px]"
            />
          </div>
          <div>
            <label htmlFor="pki-ampel-beob" className="text-[12px] uppercase tracking-[0.16em] font-semibold text-sand-700">
              Was hast du beobachtet? *
            </label>
            <textarea
              id="pki-ampel-beob"
              value={beobachtung}
              onChange={(e) => setBeobachtung(e.target.value)}
              data-testid="pki-ampel-beobachtung"
              placeholder="z. B. Herr Müller wirkt unruhig, isst nichts, wirkt müde…"
              rows={5}
              className="mt-1 w-full px-3 py-2.5 rounded-xl bg-white border border-cream-300 focus:border-brand-darkred focus:outline-none text-[15px] leading-relaxed resize-none"
            />
            <div className="mt-1.5 text-[11.5px] text-sand-500">
              Kein Diagnose-Tool. Die KI liefert eine Fach-Einschätzung — Freigabe bleibt bei dir.
            </div>
          </div>
          {error && (
            <div data-testid="pki-ampel-error" className="rounded-xl border border-brand-darkred/40 bg-peach-50 p-3 text-[13px] text-brand-darkred">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="pki-ampel-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white h-12 text-[15px] font-semibold transition-colors disabled:opacity-70"
          >
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> Ampel wird bewertet…</> :
                       <><Send className="h-4 w-4" strokeWidth={1.9} /> Ampel bewerten</>}
          </button>
        </form>
      )}

      {result && (() => {
        const style = AMPEL_STYLES[result.ampel] || AMPEL_STYLES.gelb;
        const { Icon } = style;
        return (
          <div data-testid="pki-ampel-result" className="space-y-4">
            <div className={`rounded-3xl border-2 p-5 sm:p-6 ${style.box}`}>
              <div className="flex items-center gap-3">
                <div className={`h-11 w-11 rounded-2xl ${style.bar} text-white inline-flex items-center justify-center shrink-0`}>
                  <Icon className="h-6 w-6" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <div className={`text-[10.5px] uppercase tracking-[0.22em] font-bold ${style.accent}`}>
                    Ampel · {kunde || "Bewohner"}
                  </div>
                  <div className="font-display text-3xl text-sand-800 leading-none mt-0.5">
                    {style.label}
                  </div>
                  <div className="text-[12px] text-sand-600 mt-1">{style.hint}</div>
                </div>
              </div>
              {result.zusammenfassung && (
                <p className="mt-4 text-[14px] text-sand-800 leading-relaxed">
                  {result.zusammenfassung}
                </p>
              )}
            </div>

            {result.sofort?.length > 0 && (
              <div className="rounded-2xl bg-white border border-cream-300 p-4 sm:p-5">
                <h3 className="text-[11px] uppercase tracking-[0.22em] font-bold text-sand-700 mb-3">
                  Sofort tun
                </h3>
                <ul className="space-y-2.5">
                  {result.sofort.map((s, i) => (
                    <li key={i} data-testid="pki-ampel-sofort-item" className="flex items-start gap-2.5 text-[14px] text-sand-800 leading-snug">
                      <span className="inline-flex h-5 w-5 rounded-full bg-brand-darkred text-white text-[10.5px] font-bold items-center justify-center shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.meldung && (
              <div data-testid="pki-ampel-meldung" className="rounded-2xl border-2 border-brand-darkred/30 bg-brand-rose/40 p-4 sm:p-5 flex items-start gap-3">
                <Bell className="h-5 w-5 text-brand-darkred shrink-0 mt-0.5" strokeWidth={1.9} />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brand-darkred">Melden an</div>
                  <div className="mt-1 text-[14px] text-sand-800 leading-relaxed">{result.meldung}</div>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={reset}
              data-testid="pki-ampel-reset"
              className="w-full inline-flex items-center justify-center rounded-full bg-white border border-cream-300 hover:border-brand-darkred/40 text-sand-800 h-12 text-[14.5px] font-semibold"
            >
              Neue Beobachtung eintragen
            </button>
          </div>
        );
      })()}
    </div>
  );
}
